import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { DashboardItem } from '@/types/dashboard'

interface DashboardsState {
  dashboards: DashboardItem[]
  selectedDashboard: DashboardItem | null
  isLoading: boolean
  error: string | undefined
}

const initialState: DashboardsState = {
  dashboards: [],
  selectedDashboard: null,
  isLoading: false,
  error: undefined,
}

const setDashboards = (
  state: DashboardsState = initialState,
  action: PayloadAction<DashboardItem[]>
) => {
  return {
    ...state,
    dashboards: action.payload
  }
}

const setSelectedDashboard = (
  state: DashboardsState = initialState,
  action: PayloadAction<DashboardItem | null>
) => {
  return {
    ...state,
    selectedDashboard: action.payload
  }
}

const addDashboard = (
  state: DashboardsState = initialState,
  action: PayloadAction<DashboardItem>
) => {
  return {
    ...state,
    dashboards: [action.payload, ...state.dashboards]
  }
}

const updateDashboardName = (
  state: DashboardsState = initialState,
  action: PayloadAction<{ id: string; name: string }>
) => {
  const updatedAt = new Date().toISOString()

  const updatedDashboards = state.dashboards.map(d =>
    d.id === action.payload.id
      ? { ...d, name: action.payload.name, updatedAt }
      : d
  )

  const updatedSelected =
    state.selectedDashboard?.id === action.payload.id
      ? { ...state.selectedDashboard, name: action.payload.name, updatedAt }
      : state.selectedDashboard

  return {
    ...state,
    dashboards: updatedDashboards,
    selectedDashboard: updatedSelected
  }
}

const removeDashboard = (
  state: DashboardsState = initialState,
  action: PayloadAction<string>
) => {
  return {
    ...state,
    dashboards: state.dashboards.filter(d => d.id !== action.payload),
    selectedDashboard:
      state.selectedDashboard?.id === action.payload ? null : state.selectedDashboard
  }
}

const setLoading = (
  state: DashboardsState = initialState,
  action: PayloadAction<boolean>
) => {
  return {
    ...state,
    isLoading: action.payload
  }
}

const setError = (
  state: DashboardsState = initialState,
  action: PayloadAction<string | undefined>
) => {
  return {
    ...state,
    error: action.payload
  }
}

const reset = () => initialState

const dashboardsSlice = createSlice({
  name: 'dashboards',
  initialState,
  reducers: {
    setDashboards,
    setSelectedDashboard,
    addDashboard,
    updateDashboardName,
    removeDashboard,
    setLoading,
    setError,
    reset
  }
})

export const dashboardsReducer = dashboardsSlice.reducer
export const dashboardsActions = dashboardsSlice.actions
